import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { NotificationFacade, RelatedCardFacade, TravelCardType } from '@ridango/components';
import { AllowedCardTypes, AppStartupService, AuthService, CardGroup, CardTypes, RelatedCard } from '@ridango/core';
import Env from '@ridango/core/env';
import { adult } from '@ridango/core/lib/data/adult-group.data';
import { ThemeFeature } from '@ridango/core/lib/enums/features.enum';
import { BaseComponent } from '@ridango/core';
import { Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'rwidget-related-card-add-and-edit',
    templateUrl: './related-card-add-and-edit.component.html',
    styleUrls: ['./related-card-add-and-edit.component.scss']
})
export class RelatedCardAddAndEditComponent extends BaseComponent implements OnInit, OnDestroy {
    selectedRelatedCard$: Observable<RelatedCard> = this.relatedCardFacade.selectedCard$;
    relatedCardsLoaded$: Observable<boolean> = this.relatedCardFacade.loaded$;
    editMode$: Observable<boolean> = this.relatedCardFacade.editMode$;
    allDiscounts$: Observable<CardGroup[]> = this.relatedCardFacade.allDiscountGroups$;
    selectedDiscount$: Observable<CardGroup> = this.relatedCardFacade.selectedDiscountGroup$;

    relatedCardForm: FormGroup;
    isEditMode: boolean;
    dropdownVisible = false;
    allDiscounts: CardGroup[];
    themeFeature = ThemeFeature;
    cardTypes = CardTypes;
    allowedCardTypes: AllowedCardTypes[];
    selectedCardType: AllowedCardTypes;

    showDiscounts: boolean = this.environment.RELATED_CARD?.SHOW_DISCOUNTS;
    hidePinField: boolean = this.environment.RELATED_CARD?.HIDE_PIN;
    showCardTypes: boolean = this.environment.RELATED_CARD?.SHOW_CARD_TYPES;
    selectedDiscount: CardGroup;

    @Input() assetsPath = 'assets';
    @Input() cardId: number;
    @Input() showRequiredText = true;
    @Input() cardType = 'U';
    @Output() onSaveSuccess = new EventEmitter<any>();

    constructor(
        @Inject(LOCALE_ID) public locale: string,
        @Inject('ENV') public environment: Env,
        private appConfig: AppStartupService,
        private authService: AuthService,
        private relatedCardFacade: RelatedCardFacade,
        private notification: NotificationFacade,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit() {
        this.locale = this.translate.currentLang;
        this.relatedCardFacade.resetRelatedCardStatus();

        this.editMode$.pipe(takeUntil(this.componentDestroyed$)).subscribe((value) => {
            this.isEditMode = value;

            if (!this.isEditMode) {
                this.relatedCardFacade.setSelectedDiscount(adult);
            }
        });

        this.relatedCardForm = new FormGroup({
            card_nr: new FormControl({ value: '', disabled: this.isEditMode }, [
                Validators.required,
                Validators.pattern('[0-9]*')
            ]),
            card_pin: new FormControl('', [
                Validators.required,
                Validators.pattern('[0-9]*'),
                Validators.minLength(4),
                Validators.maxLength(4)
            ]),
            name: new FormControl('', [Validators.required]),
            groups: new FormControl('') // adult
        });

        if (this.showCardTypes) {
            this.addCardTypeSelection();
        }

        if (this.showDiscounts) {
            this.allDiscounts$.pipe(takeUntil(this.componentDestroyed$)).subscribe((discounts) => {
                const webDiscountGroups = this.appConfig.getPropertyByCode('WEB_DISCOUNT_GROUPS')?.value;
                /*
                 * WEB_DISCOUNT_GROUPS could be one of the following:
                 * - a string of ids separated by commas (no, not an array of numbers, that would make too much sense).
                 * - undefined (maybe it's some kind of a statement, who knows).
                 * - 'null' (yes, string 'null'). Why? Who knows. I blame PHP.
                 */
                if (webDiscountGroups && webDiscountGroups !== 'null') {
                    this.allDiscounts = [
                        // don't forget about a virtual "Adult" group
                        discounts.find((g) => g.id === null),
                        ...webDiscountGroups
                            ?.split(',')
                            .map((groupId) => discounts.find((g) => g.group_id === Number(groupId)))
                    ].filter(Boolean);
                } else {
                    this.allDiscounts = discounts;
                }

                if (discounts.length === 0) {
                    this.relatedCardFacade.fetchCardsDiscountGroups();
                }

                if (!this.isEditMode) {
                    this.relatedCardForm.controls.groups.setValue('');
                }
            });

            this.selectedDiscount$.pipe(takeUntil(this.componentDestroyed$)).subscribe((discount) => {
                if (discount) {
                    this.relatedCardForm.controls.groups.setValue(discount.group_id);
                }
            });
        } else {
            this.relatedCardForm.removeControl('groups');
        }

        if (this.environment.THEME_NAME === this.themeFeature.VL) {
            this.relatedCardForm.get('name').clearValidators();
        }

        if (this.hidePinField) {
            this.relatedCardForm.removeControl('card_pin');
        }

        if (this.isEditMode) {
            this.relatedCardForm.removeControl('card_pin');

            this.selectedRelatedCard$.pipe(takeUntil(this.componentDestroyed$)).subscribe((card: RelatedCard) => {
                if (!card) {
                    this.relatedCardFacade.fetchAllRelatedCards();
                }
            });

            this.relatedCardsLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe((loaded) => {
                if (loaded) {
                    this.relatedCardFacade
                        .getRelatedCardById(this.cardId)
                        .pipe(takeUntil(this.componentDestroyed$))
                        .subscribe((card: RelatedCard) => {
                            if (card) {
                                if (this.showDiscounts && card.groups) {
                                    this.relatedCardFacade.setSelectedDiscount(this.findSelectedGroup(card));
                                } else if (this.showDiscounts && !card.groups) {
                                    this.relatedCardFacade.setSelectedDiscount(adult);
                                }

                                this.relatedCardFacade.setSelectedRelatedCard(card);
                                this.relatedCardForm.controls['name'].setValue(card.name);
                            }
                        });
                }
            });
        }

        this.relatedCardFacade.isActionCompleted$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((isActionCompleted) => {
                if (isActionCompleted) {
                    this.relatedCardFacade.error$.pipe(take(1)).subscribe((error) => {
                        if (error) {
                            this.handleError(error);
                        } else {
                            if (this.isEditMode) {
                                this.notification.success(
                                    this.translate.instant('LOCAL_RELATED_CARD_CHANGED_SUCCESS'),
                                    {
                                        showAfterRouteChange: true
                                    }
                                );
                            } else {
                                this.notification.success(this.translate.instant('LOCAL_RELATED_CARD_ADDED_SUCCESS'), {
                                    showAfterRouteChange: true
                                });
                            }
                            this.onSaveSuccess.emit();
                        }
                    });
                }
            });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.relatedCardFacade.setSelectedRelatedCard(null);
        this.relatedCardFacade.setSelectedDiscount(null);
    }

    handleError(e: any) {
        if (e.error.description) {
            this.notification.error(this.translate.instant(e.error.description));
        } else {
            this.notification.error(this.translate.instant(e.error));
        }
    }

    dropdownToggle(value: boolean) {
        this.dropdownVisible = value;
    }

    findSelectedGroup(card: RelatedCard) {
        return this.allDiscounts.find((discount) => discount.group_id === +card.groups);
    }

    addCardTypeSelection() {
        const cardRegionProperty = this.appConfig.getPropertyByCode('WEB_ALLOWED_CARDS');
        if (cardRegionProperty) {
            this.allowedCardTypes = JSON.parse(cardRegionProperty.value);
            if (this.allowedCardTypes) {
                this.allowedCardTypes.forEach((t) => (t['name'] = this.translate.instant(t.translationTag)));
                this.relatedCardForm.controls.card_nr.disable();
            }
        }
    }

    cardTypeSelected(event) {
        if (event !== this.selectedCardType) {
            this.selectedCardType = event;
            this.cardType = this.selectedCardType.type;
            this.relatedCardForm.controls.card_nr.reset();
            this.relatedCardForm.controls.card_nr.enable();

            if (this.selectedCardType.cardNumberLength) {
                this.relatedCardForm.controls['card_nr'].setValidators([
                    Validators.minLength(this.selectedCardType.cardNumberLength),
                    Validators.maxLength(this.selectedCardType.cardNumberLength)
                ]);
            }
        }
    }

    submitCardForm() {
        if (this.isEditMode) {
            this.changeCard();
        } else {
            this.addCard();
        }
    }

    changeCard() {
        if (this.relatedCardForm.valid) {
            const payload = this.relatedCardForm.value;
            delete payload['card_nr'];

            if (this.environment.IS_CORPORATE) {
                payload.company_id = this.authService.getLoggedInUserCompanyId();
            }

            this.relatedCardFacade.saveEditedRelatedCard(this.cardId, payload);
        }
    }

    addCard() {
        if (this.relatedCardForm.valid) {
            let cardPrefix;

            if (
                this.selectedCardType &&
                this.cardType !== TravelCardType.TravelCardU &&
                this.cardType !== TravelCardType.TravelCardUTN2
            ) {
                cardPrefix = this.selectedCardType.prefix;
            } else if (this.appConfig.getPropertyByCode('CARD_NUMBER_PREFIX')) {
                cardPrefix = this.appConfig.getPropertyByCode('CARD_NUMBER_PREFIX').value;
            }

            const payload = this.relatedCardForm.value;

            payload.card_nr = (cardPrefix ? cardPrefix : '') + payload.card_nr;
            payload.card_type = this.cardType;
            payload.region_id = this.appConfig.getRegion().region_id;

            if (this.environment.IS_CORPORATE) {
                payload.company_id = this.authService.getLoggedInUserCompanyId();
            }

            this.relatedCardFacade.saveNewRelatedCard(payload);
        }
    }
}
